/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '../../../components/buttons/current/TertiaryButton';
import {SavedMapViewType} from '../../../types/types';
import {BasemapGalleryHeaderContent} from '../../layers/BasemapGalleryHeaderContent';
import {useCurrentPanelStyles} from '../../layers/currentPanelStyles';
import SavedMapViewsChooserList from './SavedMapViewsChooserList';
import SavedMapViewsCurrentInput from './SavedMapViewsCurrentInput';

export type SavedMapViewsCurrentCardProps = {
  isVisible?: boolean;
  isFormShown: boolean;
  isTracking?: boolean;
  panelTop?: number;
  rightOffset?: number;
  savedMapViews: SavedMapViewType[];
  mapViewToEdit?: SavedMapViewType;
  onOpenSaveForm: () => void;
  onClose: () => void;
  onCancel: Function;
  onSave: Function;
  onSetMapView: Function;
  onDelete: Function;
  onEdit: Function;
  onSetDefaultMapView: Function;
};

const useStyles = makeStyles(() => ({
  // the saved views list scrolls within the card's capped height ...
  card : {
    overflowY : 'auto'
  }
}));

/**
 * Renders the current theme's Tracking map views card: a Map Layers styled
 * floating card whose header carries the Save Current View action (while
 * the saved views list is shown) and a close button, over either the saved
 * views list or the name form.
 *
 * ```typescript
 *
 *   import {SavedMapViewsCurrentCard} from
 *     '@ion/dls/src/maps/components/views/SavedMapViewsCurrentCard';
 *   ...
 *
 *   <SavedMapViewsCurrentCard isVisible
 *     isFormShown={false}
 *     isTracking
 *     panelTop={80}
 *     rightOffset={24}
 *     savedMapViews={savedMapViews}
 *     onOpenSaveForm={onOpenSaveForm}
 *     onClose={onClose}
 *     onCancel={onCancel}
 *     onSave={onSave}
 *     onSetMapView={onSetMapView}
 *     onDelete={onDelete}
 *     onEdit={onEdit}
 *     onSetDefaultMapView={onSetDefaultMapView} />
 *
 * ```
 *
 * @param   isVisible           Shows the card when `true`.
 * @param   isFormShown         Shows the name form instead of the list.
 * @param   isTracking          Shows the list's set-default action.
 * @param   panelTop            The card's viewport top, in pixels.
 * @param   rightOffset         The card's viewport right offset, in pixels.
 * @param   savedMapViews       The saved map views.
 * @param   mapViewToEdit       The saved map view being renamed, if any.
 * @param   onOpenSaveForm      Invoked by the Save Current View action.
 * @param   onClose             Invoked by the close button.
 * @param   onCancel            Invoked by the name form's Cancel button.
 * @param   onSave              Invoked by the name form's Done button.
 * @param   onSetMapView        Invoked with the saved view clicked in the list.
 * @param   onDelete            Invoked by a list item's delete action.
 * @param   onEdit              Invoked by a list item's edit action.
 * @param   onSetDefaultMapView Invoked by a list item's set-default action.
 * @returns                     The current theme's map views card.
 */
export const SavedMapViewsCurrentCard = ({
  isVisible,
  isFormShown,
  isTracking,
  panelTop,
  rightOffset,
  savedMapViews,
  mapViewToEdit,
  onOpenSaveForm,
  onClose,
  onCancel,
  onSave,
  onSetMapView,
  onDelete,
  onEdit,
  onSetDefaultMapView
}: SavedMapViewsCurrentCardProps) => {
  const panelClasses = useCurrentPanelStyles({panelTop, rightOffset});
  const classes      = useStyles();

  return (
    <div style={{display: !isVisible ? 'none' : 'flex'}}
      className={classnames(panelClasses.panel, panelClasses.floating, classes.card)}>
      <div className={panelClasses.header}>
        <BasemapGalleryHeaderContent isCurrent
          label={translate('maps:mapViews.header')}
          action={!isFormShown && (
            <TertiaryButton onClick={onOpenSaveForm}>
              {translate('maps:mapViews.save')}
            </TertiaryButton>
          )}
          onClose={onClose} />
      </div>
      {isFormShown ? (
        <SavedMapViewsCurrentInput savedMapViews={savedMapViews}
          mapViewToEdit={mapViewToEdit}
          onCancel={onCancel}
          onSave={onSave} />
      ) : (
        <SavedMapViewsChooserList savedMapViews={savedMapViews}
          isTracking={isTracking}
          onSetMapView={onSetMapView}
          onDelete={onDelete}
          onEdit={onEdit}
          onSetDefaultMapView={onSetDefaultMapView} />
      )}
    </div>
  );
};

export default SavedMapViewsCurrentCard;
